import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import { MemberNameWithScar } from '@/components/ui/member-name-with-scar';
import { useMemberDisplay } from '@/hooks/use-member-display';
import { INACTIVE_ACCOUNT_STATUS_LABELS } from '@/utils/account-status';
import { cn } from '@/utils/cn';
import type { MemberDisplay } from '@/utils/member-display';
import type { Creator } from '@/types';

type Collector = NonNullable<NonNullable<Creator['expand']>['collector']>;

function photoUrlOf(c: Collector): string | undefined {
  return c.photo ? `/api/files/team_members/${c.id}/${c.photo}` : undefined;
}

function NameAndStatus({ display }: { display: MemberDisplay }): ReactNode {
  const name = display.fullName ?? display.name;
  const inactive = !!display.isInactive;
  const label = display.accountStatus
    ? INACTIVE_ACCOUNT_STATUS_LABELS[display.accountStatus]
    : undefined;
  const nameCls = cn(
    'truncate text-[11px] text-text-secondary',
    inactive && 'line-through opacity-60'
  );
  return (
    <>
      <MemberNameWithScar
        name={<span className={nameCls}>{name}</span>}
        hasRedeemed={display.redemptionScar}
      />
      {inactive && label && (
        <span className="text-[10px] text-error">· {label}</span>
      )}
    </>
  );
}

function CollectorButton({ c }: { c: Collector }): ReactNode {
  const navigate = useNavigate();
  const display = useMemberDisplay(c);
  const name = display.fullName ?? display.name;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/progress/${c.id}`);
      }}
      className="flex items-center gap-xs text-left hover:text-text-primary"
    >
      <Avatar src={photoUrlOf(c)} name={name} size="sm" />
      <NameAndStatus display={display} />
    </button>
  );
}

export function CollectorCell({ creator }: { creator: Creator }): ReactNode {
  const c = creator.expand?.collector;
  if (!c) return <span className="text-text-tertiary/30">—</span>;
  return <CollectorButton c={c} />;
}
